import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Leads from './pages/Leads';
import CallHistory from './pages/CallHistory';
import Agents from './pages/Agents';
import AdminPanel from './pages/AdminPanel';
import Properties from './pages/Properties';
import Meetings from './pages/Meetings';
import Deals from './pages/Deals';
import Payroll from './pages/Payroll';
import Reports from './pages/Reports';
import Settings from './pages/Settings';
import AuditLogs from './pages/AuditLogs';
import Pipeline from './pages/Pipeline';
import Groups from './pages/Groups';
import Profile from './pages/Profile';
import MetaSettings from './pages/MetaSettings';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const pageTitles = {
    '/': 'Dashboard',
    '/pipeline': 'Pipeline',
    '/leads': 'Leads Management',
    '/calls': 'Call History',
    '/agents': 'Team Management',
    '/admin': 'Admin Panel',
    '/properties': 'Property Inventory',
    '/meetings': 'Meetings',
    '/deals': 'Deals & Commission',
    '/payroll': 'Payroll & Salary',
    '/reports': 'Reports & Export',
    '/settings': 'Settings & Branding',
    '/audit-logs': 'Audit Logs',
  };
  const title = pageTitles[location.pathname] || 'Dashboard';

  return (
    <div className="min-h-screen bg-dark-950 flex">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className="flex-1 flex flex-col min-w-0">
        <Header onMenuToggle={() => setSidebarOpen(!sidebarOpen)} title={title} />
        <main className="flex-1 p-4 lg:p-6 overflow-y-auto">
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/groups" element={<Groups />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/meta-settings" element={<MetaSettings />} />
            <Route path="/calls" element={<CallHistory />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/payroll" element={<Payroll />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/audit-logs" element={<AuditLogs />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          } />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

function LoginPage() {
  const { user } = useAuth();
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

export default App;
